import { Controller } from "@hotwired/stimulus"
import Mustache  from 'mustache';
import { useEcharts } from "../../mixins/useEcharts";
import { replaceFormatters } from "../../lib/echarts_formatters.js";
import { EQUALIZER_STEP_MS, equalizerOptions, equalizerFrame } from "../../lib/charts/equalizer.js";

// Connects to data-controller="chart--basic"
export default class extends Controller {
  static values = {
    lockableTooltips: {type: Boolean, default: false},
    loading: Boolean,
    theme:   { type: Object, default: {} },
    options: { type: Object, default: {} },
    tooltip: { type: Object, default: {} }
  }
  static targets = ['chart', 'tooltip']

  get tooltip() {
    const tooltip = this.tooltipValue
    const tooltipTarget = this.tooltipTarget
    return {
      trigger: 'axis',
      triggerOn: 'mousemove|click',
      axisPointer: {
        type: 'shadow'
      },
      className: this.tooltipClasses,
      formatter: function(params) {
        return  Mustache.render(tooltipTarget.innerHTML, tooltip[params[0].axisValue]);
      }
    };
  }

  connect() {
    useEcharts(this);
    this.replaceFormattersWithFunctions();
    this.chart = this.initializeChart(this.chartTarget, this.themeValue, { renderer: 'svg' });
    this.draw();
    if(this.lockableTooltipsValue) {
      this.enableLockableTooltips()
    }
  }

  // While loading the equalizer stands in for the chart; new values wait for loading to end.
  optionsValueChanged() {
    if (!this.loadingValue) this.rerender();
  }

  tooltipValueChanged() {
    if (!this.loadingValue) this.rerender();
  }

  loadingValueChanged() {
    this.rerender();
  }

  // Values rewritten after connect (a page hydrating from the API) redraw the chart in place.
  rerender() {
    if (!this.chart) return;
    this._modifiedOptions = null;
    this.replaceFormattersWithFunctions();
    this.draw();
  }

  draw() {
    clearTimeout(this.equalizerStep);
    if (!this.loadingValue) return this.chart.setOption(this.options, { notMerge: true });
    this.chart.setOption(equalizerOptions(this.options), { notMerge: true });
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) this.stepEqualizer();
  }

  // Ends on its own once the chart is disposed.
  stepEqualizer = () => {
    this.equalizerStep = setTimeout(() => {
      if (!this.chart) return;
      this.chart.setOption({ series: equalizerFrame(this.chart.getOption().series) });
      this.stepEqualizer();
    }, EQUALIZER_STEP_MS);
  }

  replaceFormattersWithFunctions() {
    const optionsCopy = this.options;
    replaceFormatters(optionsCopy);
    this._modifiedOptions = optionsCopy;
  }

  // Override the original options getter to use the modified options
  get options() {
    if (this._modifiedOptions) {
      return this._modifiedOptions;
    }

    const optionsCopy = JSON.parse(JSON.stringify(this.optionsValue));
    // Series keyed by position, so a redraw glides from what is on screen instead of starting over.
    optionsCopy.series.forEach((series, index) => { series.id = String(index) });
    return {
      tooltip: this.tooltip,
      ...optionsCopy
    };
  }
}
